import React from 'react';
import { Navigate, useLocation, useNavigate, matchPath } from 'react-router-dom';
import { LocalStorageUtil } from 'utils/local-storage.util';
import { ACCESS_TOKEN_LOCAL_STORAGE_KEY, SignupStatus } from 'constants/user';
import { useUserContext } from 'common/contexts/UserContext';
import LoadingComponent from 'components/LoadingComponent';
import { Routes } from 'constants/pages';
import { RegionContextProvider } from 'common/contexts/RegionContext';

interface Props {
  children: React.ReactNode;
  grouping?: 'signup';
}

export const PrivateRoute: React.FC<Props> = ({ children, grouping }) => {
  const token = LocalStorageUtil.get(ACCESS_TOKEN_LOCAL_STORAGE_KEY);
  const { user, fetchUser } = useUserContext();
  const location = useLocation();
  const navigate = useNavigate();

  const route = `${Routes.SignIn}?redirect=${location.pathname}${location.search}`;

  React.useEffect(() => {
    if (token && !user) {
      fetchUser().catch(() => {
        localStorage.clear();
        navigate(route);
      });
    }
  }, [token, user, fetchUser, navigate, route]);

  if (!token) {
    return <Navigate to={route} replace />;
  }

  if (user?.signup_status === SignupStatus.MembershipRevoked) {
    return <Navigate to={`${Routes.ResetPassword}?revoked=true`} replace />;
  }

  if (user?.signup_status === SignupStatus.AwaitingCompanyCreation && grouping !== 'signup') {
    return <Navigate to={Routes.JoinCompany} replace />;
  }

  if (user?.signup_status === SignupStatus.AwaitingAdminConfirmation) {
    const isAwaitingPage = matchPath(Routes.AwaitingAdmin, location.pathname);
    if (!isAwaitingPage) {
      return <Navigate to={Routes.AwaitingAdmin} replace />;
    }
  }

  if (user?.signup_status === SignupStatus.AwaitingShipperValidation && grouping !== 'signup') {
    return <Navigate to={Routes.Waitlist} replace />;
  }

  if (token && !user) {
    return (
      <div className="w-screen h-screen flex justify-center items-center">
        <LoadingComponent isLoading size={55} />
      </div>
    );
  }

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <RegionContextProvider>{children}</RegionContextProvider>;
};
PrivateRoute.displayName = 'PrivateRoute';
